import { memo, useEffect, useRef, useState } from 'react';
import { FeatureGroup, LayerGroup, LayersControl, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';

import { area as turfArea } from '@turf/area';
import { length as turfLength } from '@turf/length';
import classnames from 'classnames';
import L from 'leaflet';
import isEmpty from 'lodash.isempty';
import { v4 as uuidv4 } from 'uuid';

import { ConditionalWrapper, reverseCoordinates } from '../../helpers/map';

import { GEOMETRY_TYPE } from '../../constants/map';

import './styles.scss';

import BaseLayers from './BaseLayers';
import AppMapContainer from './Container';
import CustomLayers from './CustomLayers';
import EditButtons from './EditButtons';
import Feature from './Feature';
import {
  EDIT_CONTROL_DRAW_OPTIONS,
  EDIT_CONTROL_POSITION,
  LAYERS_CONTROL_POSITION,
  SHAPE_OPTIONS,
  ZOOM_MAX,
  customizeButtonLabels
} from './constants';

const { Overlay } = LayersControl;

function GeofenceLayer({
  color,
  editing,
  geofence,
  grazing,
  isNew,
  onEdit,
  handleShapeSizes,
  handleCancelEditing,
  handleCreate,
  handleDelete,
  handleSaveEditing, // not used, remained in case we want to change the behavior
  setSubmittable
}) {
  const map = useMap();

  const featureGroupRef = useRef(null);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [state, setState] = useState({
    currentColor: color,
    enableDraw: !!isNew,
    features: { features: [] },
    features_master: {},
    features_sites: { features: [] }
  });

  const initializeFeatureGroup = () => {
    if (!featureGroupRef?.current) return;

    const fg = featureGroupRef.current;

    fg.clearLayers();

    if (isEmpty(geofence?.geo_json)) return;

    geofence.geo_json?.properties?.radius
      ? drawCircle(fg, geofence.geo_json?.geometry.coordinates, geofence.geo_json?.properties.radius)
      : drawPolygon(fg, geofence.geo_json);
  };

  const handleFullScreenChange = () => {
    setIsFullScreen(!!document.fullscreenElement);
  };

  const clickEditButton = () => {
    if (!editing) return;

    const editButton = document.querySelector('a.leaflet-draw-edit-edit');

    if (editButton) {
      editButton.click();
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  useEffect(() => {
    if (!geofence) return;

    customizeButtonLabels();
    initializeFeatureGroup();
    tick();
  }, [geofence]);

  useEffect(() => {
    toggleEditMode();
    setTimeout(clickEditButton, 500);
  }, [editing]);

  useEffect(() => {
    handleColorChange();
  }, [color]);

  function drawCircle(fg, coordinates, radius) {
    const [lat, lng] = [coordinates?.at(1), coordinates?.at(0)];
    const layer = L.circle([lat, lng], {
      ...SHAPE_OPTIONS,
      color: state.currentColor,
      fillColor: state.currentColor,
      radius
    });

    fg.addLayer(layer);
  }

  function drawPolygon(fg, geoJson) {
    L.geoJSON(geoJson, {
      style: {
        ...SHAPE_OPTIONS,
        color: state.currentColor,
        fillColor: state.currentColor
      }
    }).eachLayer(layer => fg.addLayer(layer));
  }

  function toggleEditMode() {
    if (!map || state.editing === editing) return;

    const mapContainer = map?.getContainer();

    editing ? mapContainer.classList.add('editing') : mapContainer.classList.remove('editing');
  }

  function handleColorChange() {
    if (!featureGroupRef.current) return;

    if (state.currentColor !== color) {
      featureGroupRef.current.eachLayer(layer => {
        if (typeof layer.setStyle === 'function') {
          layer.setStyle({ color, fillColor: color, zIndex: 9999 });
        }
      });

      setState(prevState => ({ ...prevState, currentColor: color }));
    }
  }

  function calculateBoundsForCircle(geoJSON) {
    let bounds;
    const { coordinates } = geoJSON.geometry;
    const radius = geoJSON.properties?.radius || 0;

    if (coordinates?.length === 2 && radius > 0) {
      const [lng, lat] = coordinates;
      const center = L.latLng(lat, lng);

      bounds = center.toBounds(radius * 2);

      return bounds;
    }
  }

  function calculateBoundsForPolygon(geoJson) {
    const group = new L.geoJson(geoJson);
    let bounds = group.getBounds();

    if (bounds.isValid()) {
      const corner2 = L.latLng(parseFloat(bounds._southWest?.lat), parseFloat(bounds._southWest?.lng));
      const corner1 = L.latLng(parseFloat(bounds._northEast?.lat), parseFloat(bounds._northEast?.lng));

      bounds = L.latLngBounds(corner1, corner2);

      return bounds;
    }
  }

  async function calculateMapBounds(geoData) {
    const source = geoData?.geometry ? geoData : geoData?.features?.at(0);

    if (!source) return;

    const bounds =
      source.geometry?.type === GEOMETRY_TYPE.POINT && source.properties?.radius
        ? calculateBoundsForCircle(source)
        : calculateBoundsForPolygon(source);

    if (map) {
      const center = bounds.getCenter();
      map?.setView(center, Math.min(map?.getBoundsZoom(bounds), ZOOM_MAX));
    }
  }

  async function tick() {
    if (!geofence && !geofence?.farm_id) return;

    const geofenceClone = isNew ? null : structuredClone(geofence);

    if (!isNew && geofenceClone?.geo_json?.geometry?.type) {
      const coordinates = structuredClone(
        geofenceClone?.geo_json?.geometry?.type === GEOMETRY_TYPE.POINT
          ? geofenceClone?.geo_json?.geometry?.coordinates
          : geofence.geo_json?.geometry?.coordinates?.at(0)
      );

      if (geofenceClone?.geo_json?.geometry?.type === GEOMETRY_TYPE.POINT) {
        geofenceClone.geo_json.geometry.coordinates = reverseCoordinates(coordinates);
      } else {
        geofenceClone.geo_json.geometry.coordinates[0] = reverseCoordinates(coordinates);
      }
    }

    const features = { type: 'FeatureGroup', features: [] };
    const masterGeofences = { type: 'FeatureCollection', features: [] };
    const sites = { type: 'Feature', features: [] };

    const geofenceFeaturesClone = structuredClone(geofence?.features);
    const sitesClone = structuredClone(geofence?.sites);

    geofenceFeaturesClone?.forEach(f => {
      const target = f.is_master ? masterGeofences.features : features.features;
      target.push({ ...f.geo_json, color: f.color, id: uuidv4() });
    });

    sitesClone?.forEach(s => {
      sites.features.push({ ...s.geo_json, color: s.color, id: uuidv4() });
    });

    setState(prevState => ({
      ...prevState,
      features: { ...features, features: features.features.filter(f => f.properties?.id !== geofence.id) },
      features_master: {
        ...masterGeofences,
        features: masterGeofences.features.filter(f => f.properties?.id !== geofence.id)
      },
      features_sites: sites
    }));

    const center = geofence?.geo_json?.type ? geofence?.geo_json : masterGeofences;
    await calculateMapBounds(center);
  }

  function calculateShapeSizes(geoJson) {
    const radius = geoJson?.properties?.radius;
    const area = radius ? (Math.PI * Math.pow(radius, 2)) / 10_000 : turfArea(geoJson) * 0.0001; // 1 square meter = 0.0001 hectares
    const perimeter = radius ? (2 * Math.PI * radius) / 1_000 : turfLength(geoJson, { units: 'kilometers' });

    handleShapeSizes?.({ size: area.toFixed(2), circumference: perimeter.toFixed(2) });
  }

  function handleCreateShape(e) {
    setState(prevState => ({ ...prevState, enableDraw: !!isNew, leaflet_id: e.layer._leaflet_id }));

    const layers = L.layerGroup().addLayer(e.layer);

    layers.eachLayer(layer => {
      if (handleCreate) {
        const geoJson = layer.toGeoJSON();

        geoJson.properties.radius = layer._mRadius ? layer._mRadius : layer._radius;

        calculateShapeSizes(geoJson);

        handleCreate(geoJson);
      }
    });

    setSubmittable(true);
  }

  function handleEditShape(layerGroup) {
    layerGroup.layers.eachLayer(layer => {
      if (onEdit) {
        const geoJson = layer.toGeoJSON();

        if (layer._mRadius) {
          geoJson.properties.radius = layer._mRadius;
        } else if (layer._radius) {
          geoJson.properties.radius = layer._radius;
        }

        calculateShapeSizes(geoJson);

        onEdit(geoJson);
      }
    });

    setState(prevState => ({ ...prevState, enableDraw: false }));
  }

  function handleDeleteShape() {
    setState(prevState => ({ ...prevState, enableDraw: true }));
    handleShapeSizes?.({ size: 0, circumference: 0 });

    if (handleDelete) handleDelete();
  }

  /**
   * Not used, remained to keep the structure and if we change the behavior
   */
  function handleSave() {
    handleSaveEditing();
    exitFullScreenMode();
  }

  function exitFullScreenMode() {
    if (map && map?.fullscreenControl) {
      document.exitFullscreen();
    }
  }

  return (
    <LayersControl position={LAYERS_CONTROL_POSITION}>
      <BaseLayers />
      <CustomLayers
        features={state.features}
        features_master={state.features_master}
        features_sites={state.features_sites}
        grazing={grazing}
      />

      <ConditionalWrapper
        condition={!isNew}
        wrapper={children => (
          <Overlay checked name="Current Pasture">
            {children}
          </Overlay>
        )}>
        <LayerGroup>
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              draw={{
                ...EDIT_CONTROL_DRAW_OPTIONS,
                circle: editing &&
                  state.enableDraw && {
                    shapeOptions: { ...SHAPE_OPTIONS, color: state.currentColor, fillColor: state.currentColor }
                  },
                polygon: editing &&
                  state.enableDraw && {
                    shapeOptions: { ...SHAPE_OPTIONS, color: state.currentColor, fillColor: state.currentColor }
                  }
              }}
              edit={{ featureGroup: featureGroupRef?.current, edit: true, remove: true }}
              position={EDIT_CONTROL_POSITION}
              onCreated={handleCreateShape}
              onDeleted={handleDeleteShape}
              onEdited={handleEditShape}
            />

            {geofence?.geo_json?.type && !isNew && (
              <Feature
                color={state.currentColor}
                geometry={geofence.geo_json.geometry.coordinates}
                id={geofence?.id}
                type={geofence.geo_json.geometry.type}
              />
            )}
          </FeatureGroup>
        </LayerGroup>

        {isFullScreen && editing && (
          <EditButtons handleCancel={handleCancelEditing} handleReturn={exitFullScreenMode} />
        )}
      </ConditionalWrapper>
    </LayersControl>
  );
}

function Map({
  color,
  editing,
  geofence,
  grazing,
  isNew,
  handleCancelEditing,
  handleCreate,
  handleDelete,
  handleSaveEditing,
  handleShapeSizes,
  onEdit,
  setSubmittable
}) {
  return (
    <AppMapContainer height={400} className={classnames({ 'edit-controls-visible': isNew })}>
      <GeofenceLayer
        color={color}
        editing={editing}
        geofence={geofence}
        grazing={grazing}
        isNew={isNew}
        onEdit={onEdit}
        handleCancelEditing={handleCancelEditing}
        handleCreate={handleCreate}
        handleDelete={handleDelete}
        handleSaveEditing={handleSaveEditing}
        handleShapeSizes={handleShapeSizes}
        setSubmittable={setSubmittable}
      />
    </AppMapContainer>
  );
}

export default memo(Map);
